/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Anchored host for the chatroom profile POPOVER. Reads the shared chat-room
// shell view-model (`miniProfileUserId` + `miniProfileAnchor`), positions the
// `ProfileCard` next to the tapped avatar via the existing anchor geometry,
// and dismisses on scrim-tap / Escape. Tapping the name escalates to the full
// profile modal through the VM (`openUserProfile`). The host injects a
// platform-specific `load` (peer-profile fetch) + `buildActions`.
//
// Mirrors `MiniProfileOverlay`'s mount contract: rendered inside the app's
// root chat-overlay host as a full-bleed `absolute inset-0` layer, so the
// card's absolute coordinates are viewport-relative.

import type React from 'react';
import { useEffect, useState } from 'react';
import { Platform, Pressable, View, type ViewStyle } from 'react-native';

import { WebPortal } from '../chat/composer/WebPortal';
import type { ChatRoomShellViewModel } from '../chat/useChatRoomShellViewModel';
import { PROFILE_CARD_WIDTH, ProfileCard } from './ProfileCard';
import type { ProfileMoreMenuProps } from './ProfileMoreMenu';
import { useMiniProfileAnchor } from './useMiniProfileAnchor';
import type {
  ProfileActionsFactory,
  ProfileView,
  ProfileViewCacheReader,
  ProfileViewLoader,
} from './profileViewTypes';

export interface ProfileCardOverlayProps {
  readonly vm: ChatRoomShellViewModel;
  readonly load: ProfileViewLoader;
  /** Optional synchronous cache peek — shows persisted data instantly on repeat
   *  opens while `load` refreshes in the background. */
  readonly loadCached?: ProfileViewCacheReader | undefined;
  readonly buildActions?: ProfileActionsFactory | undefined;
  /** Builds the ⋯ "More" menu (groups + actions) for a given user. */
  readonly buildMoreMenu?: ((userId: string) => ProfileMoreMenuProps | undefined) | undefined;
}

/** Estimated card height for viewport clamping (the card auto-sizes). */
const PROFILE_CARD_HEIGHT = 238;

export const ProfileCardOverlay: React.FC<ProfileCardOverlayProps> = ({
  vm,
  load,
  loadCached,
  buildActions,
  buildMoreMenu,
}) => {
  const userId = vm.state.miniProfileUserId;
  const anchorRef = vm.state.miniProfileAnchor;

  if (userId === null || anchorRef === null) {
    return null;
  }

  const dismiss = (): void => vm.handlers.closeOverlay('miniProfile');

  // Portaled to <body> on web (passthrough on native) so the card's absolute
  // anchor coordinates are viewport-relative regardless of where this host is
  // mounted in the tree (e.g. below the 56px TopBar).
  return (
    <WebPortal>
      <View className="absolute inset-0" pointerEvents="box-none">
        <Pressable
          className="absolute inset-0 bg-transparent"
          accessibilityRole="button"
          accessibilityLabel="Dismiss profile"
          onPress={dismiss}
        />
        <AnchoredProfileCard
          key={userId}
          userId={userId}
          anchorRef={anchorRef as React.RefObject<unknown>}
          load={load}
          {...(loadCached !== undefined ? { loadCached } : {})}
          actions={buildActions?.(userId) ?? {}}
          moreMenu={buildMoreMenu?.(userId)}
          onViewProfile={() => vm.handlers.openUserProfile(userId)}
          onDismiss={dismiss}
        />
      </View>
    </WebPortal>
  );
};

function AnchoredProfileCard({
  userId,
  anchorRef,
  load,
  loadCached,
  actions,
  moreMenu,
  onViewProfile,
  onDismiss,
}: {
  userId: string;
  anchorRef: React.RefObject<unknown>;
  load: ProfileViewLoader;
  loadCached?: ProfileViewCacheReader | undefined;
  actions: ReturnType<ProfileActionsFactory>;
  moreMenu: ProfileMoreMenuProps | undefined;
  onViewProfile: () => void;
  onDismiss: () => void;
}): JSX.Element | null {
  const cachedSeed = loadCached?.(userId) ?? null;
  const [view, setView] = useState<ProfileView | null>(cachedSeed);
  // Full skeleton only on a cold cache; with cached data we show it immediately
  // and surface a thin "refreshing" line instead.
  const [loading, setLoading] = useState(cachedSeed === null);
  const [refreshing, setRefreshing] = useState(cachedSeed !== null);
  const anchor = useMiniProfileAnchor({
    anchorRef,
    side: 'bottom',
    offset: 8,
    cardWidth: PROFILE_CARD_WIDTH,
    cardHeight: PROFILE_CARD_HEIGHT,
  });

  useEffect(() => {
    let alive = true;
    const cached = loadCached?.(userId) ?? null;
    // Cache hit → show persisted data instantly + refresh in the background.
    // Cache miss → full skeleton until the first fetch resolves.
    setView(cached);
    setLoading(cached === null);
    setRefreshing(cached !== null);
    void load(userId)
      .then((v) => {
        if (!alive) return;
        // Keep the cached view if the refetch yields nothing (transient miss).
        if (v !== null) setView(v);
        setLoading(false);
        setRefreshing(false);
      })
      .catch(() => {
        if (!alive) return;
        setLoading(false);
        setRefreshing(false);
      });
    return () => {
      alive = false;
    };
  }, [userId, load, loadCached]);

  // Escape dismisses on web/macOS.
  useEffect(() => {
    if (Platform.OS !== 'web' && Platform.OS !== 'macos') return;
    if (typeof window === 'undefined') return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onDismiss]);

  // Appears as soon as the anchor is measured (one frame) — the card renders a
  // shimmer skeleton while the profile fetch is in flight, so there's no wait.
  if (!anchor.rect.visible) {
    return null;
  }

  // Runtime-computed anchor coordinates → the documented inline-style fallback.
  const positionStyle: ViewStyle =
    Platform.OS === 'web' || Platform.OS === 'macos'
      ? { position: 'absolute', top: anchor.rect.top, left: anchor.rect.left }
      : {};

  return (
    <View style={positionStyle}>
      <ProfileCard
        view={view}
        loading={loading}
        refreshing={refreshing}
        actions={actions}
        moreMenu={moreMenu}
        onViewProfile={onViewProfile}
      />
    </View>
  );
}
